import { useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Step, StepLabel, Stepper } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormFactoryWithStandardLayout } from '../../../../../components/Form/FormFactory';
import {
  granularityDefaultFromFrequency,
  useAssignTemplateFormsSchema,
} from '../../../../../schemas/template-assignment-schema';
import { schemaToFormFields } from '../../../../../util/schema-utils';
import { createFormFromFieldsArray } from '../../../../../view-models/form.view-model';
import { selectedKpiCompaniesStateDCJ } from '../AllCompaniesStateJ';
import { StepperContainer, StepperSx, StepSx } from '../../../../../components/Form/FormComponents';
import { pluralizeWord } from '../../../../../util/stringUtils';
import { clientConfigFieldsAtom } from '../../../../../services/state/AppConfigStateJ';
import { MaggieFeatureFlags } from '../../../../../util/feature-flags';
import { AssignTemplateStep2 } from './AssignTemplateStep2';
import { AssignTemplateStep1 } from './AssignTemplateStep1';
import { AssignTemplateStep3 } from './AssignTemplateStep3';
import { currentStepStateTAJ, useResetTemplateAssignmentFormState } from './AssignTemplateFormState';
import { useValidateCompanyData } from './useValidateCompanyData';

export function AssignTemplateFields() {
  const { setValue } = useFormContext();
  const frequency = useWatch({ name: 'frequency' });
  const assignTemplateFormSchema = useAssignTemplateFormsSchema();
  const isConsolidated =
    (useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection as boolean) ?? false;
  const { useStarbreakerCollectionV2 } = useFlags<MaggieFeatureFlags>();
  const hideReminderThreshold = isConsolidated && useStarbreakerCollectionV2;

  const fields = schemaToFormFields(
    assignTemplateFormSchema(frequency),
    hideReminderThreshold
      ? ['templateUuid', 'frequency', 'granularity']
      : ['templateUuid', 'frequency', 'granularity', 'reminderThreshold']
  );

  const form = createFormFromFieldsArray(fields);

  useEffect(() => {
    const defaultGranularity = granularityDefaultFromFrequency(frequency);
    setValue('granularity', defaultGranularity, { shouldValidate: true });
  }, [frequency, setValue]);

  return <FormFactoryWithStandardLayout form={form} />;
}

export function AssignTemplateForm() {
  const selectedCompanies = useAtomValue(selectedKpiCompaniesStateDCJ);
  const currentStep = useAtomValue(currentStepStateTAJ);
  const resetForm = useResetTemplateAssignmentFormState();
  const validateCompanyData = useValidateCompanyData();
  const steps = useMemo(() => {
    const missingData = selectedCompanies.some((c) => !validateCompanyData(c));
    const result = [
      { label: 'Request Settings', component: <AssignTemplateStep1 /> },
      { label: 'Summary', component: <AssignTemplateStep3 /> },
    ];
    if (missingData) {
      result.splice(1, 0, {
        label: `${pluralizeWord('Company', selectedCompanies.length)} Settings`,
        component: <AssignTemplateStep2 />,
      });
    }

    return result;
  }, [selectedCompanies, validateCompanyData]);

  useEffect(() => {
    return resetForm;
  }, [resetForm]);

  return (
    <StepperContainer>
      <Stepper activeStep={currentStep} sx={StepperSx}>
        {steps.map(({ label }) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: {
            optional?: React.ReactNode;
          } = {};
          return (
            <Step key={label} {...stepProps} sx={StepSx}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <>{steps[currentStep]?.component}</>
    </StepperContainer>
  );
}
